import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { useFetch } from '@reveni-io/reveni-ui'

import { transformRegionsForMultiSelect } from '@/common/helpers/helpers'
import useRegions from '@/common/hooks/useRegions/useRegions'
import commonServices from '@/common/services/services'
import { getCarrierServices } from '@/configuration/pages/ConfigurationPage/helpers'
import { carrierServiceTypes } from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/DeliveryMethods/DeliveryMethodDetailConfiguration/constants'
import {
  getLocationRegionId,
  getSelectedRegionIds,
} from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/helpers'
import {
  getDutiesAdjustmentRate,
  getShippingMethodPayload,
} from '@/configuration/pages/ConfigurationPage/LogisticsConfiguration/ShippingMethods/ShippingMethodsDetailConfiguration/helpers'
import configurationServices from '@/configuration/services/services'
import routes from '@/Router/routes.constants'

import { initShippingMethod } from './constants'
import { transformResponseCarrierServices } from './transformers'

const useShippingMethod = ({ id, selectedShippingMethod, storeId, setToastInfo }) => {
  const isDuplicate = id?.includes('duplicate-')
  const isNew = id === 'new'

  const [hasChanges, setHasChanges] = useState(false)
  const [shippingMethod, setShippingMethod] = useState(initShippingMethod)
  const [errors, setErrors] = useState([])
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { regions: regionsData, loading: fetchingRegions } = useRegions()
  const { data: currencies, loading: fetchingCurrencies } = useFetch(commonServices.getCurrencies, {
    doInitialRequest: true,
  })

  const { fetchData: editShippingMethod, loading: editingShippingMethod } = useFetch(
    configurationServices.editShippingMethod,
    {
      doInitialRequest: false,
    }
  )
  const { fetchData: createShippingMethod, loading: creatingShippingMethod } = useFetch(
    configurationServices.createShippingMethod,
    {
      doInitialRequest: false,
    }
  )

  const {
    data: carriers,
    loading: fetchingCarriers,
    fetchData: fetchCarrierServices,
  } = useFetch(commonServices.getCarrierServices, {
    doInitialRequest: false,
    transformResponse: transformResponseCarrierServices,
  })

  const refetchCarriers = useCallback(
    method => {
      fetchCarrierServices({
        storeId,
        type: carrierServiceTypes.shipment,
        sourceRegion: getLocationRegionId(method?.location),
        destinationRegion: getSelectedRegionIds(method?.regions),
      })
    },
    [storeId, fetchCarrierServices]
  )

  useEffect(() => {
    if (!isNew && selectedShippingMethod?.id) {
      const loadedShippingMethod = {
        ...selectedShippingMethod,
        regions: transformRegionsForMultiSelect(selectedShippingMethod.regions),
        duties_adjustment_rate: getDutiesAdjustmentRate(selectedShippingMethod.duties_adjustment_rate),
        availability_criterion_ids: selectedShippingMethod.availability_criterion_ids || [],
      }

      setShippingMethod(loadedShippingMethod)
      refetchCarriers(loadedShippingMethod)

      if (isDuplicate) {
        setHasChanges(true)
      }
    } else if (isNew) {
      refetchCarriers(initShippingMethod)
    }
  }, [id, selectedShippingMethod, isDuplicate, isNew, refetchCarriers])

  const revertChanges = () => {
    if (isNew || isDuplicate) {
      navigate(routes.configurationPage.logistics.shippingMethods)

      return
    }

    setShippingMethod(selectedShippingMethod)
    setErrors([])
    setHasChanges(false)
  }

  const onChangeShippingMethod = paramsEdited => {
    const newShippingMethod = { ...shippingMethod, ...paramsEdited }

    setShippingMethod(newShippingMethod)
    setHasChanges(true)

    if ('location' in paramsEdited || 'regions' in paramsEdited) {
      refetchCarriers(newShippingMethod)
    }
  }

  const saveChanges = async shippingMethod => {
    const payload = getShippingMethodPayload(shippingMethod)

    if (isNew || isDuplicate) {
      try {
        await createShippingMethod({ storeId, shippingMethod: payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        navigate(routes.configurationPage.logistics.shippingMethods)
      } catch (error) {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      }
    } else {
      try {
        await editShippingMethod({ storeId, shippingMethod: payload })
        setToastInfo({ show: true, message: t('Cambios guardados'), type: 'success' })
        setHasChanges(false)
        navigate(routes.configurationPage.logistics.shippingMethods)
      } catch (error) {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      }
    }
  }

  return {
    shippingMethod,
    errors,
    setErrors,
    fetchingRegions,
    regions: regionsData,
    fetchingCurrencies,
    currencies,
    editingShippingMethod,
    creatingShippingMethod,
    editShippingMethod,
    createShippingMethod,
    carriers: getCarrierServices(shippingMethod?.carrier, carriers),
    fetchingCarriers: fetchingCarriers && !carriers,
    hasChanges,
    setHasChanges,
    revertChanges,
    onChangeShippingMethod,
    saveChanges,
  }
}

export default useShippingMethod
